// THIS FILE MUST NOT IMPORT ANYTHING AS IT IS SHARED WITH THE APP

// the tesla will go down to 5 amps
export const TESLA_MIN_AMPS = 5
// I know at least 40 amps is possible on a Model 3.
// I've caught the app showing 48 amps I think.
export const TESLA_MAX_AMPS = 40

// tesla charge limit can not go below 50%
export const TESLA_MIN_CHARGE_LIMIT = 50

// https://developer.tesla.com/docs/fleet-api#region
export enum TeslaRegion {
  NA = 'na',  // North America, Asia-Pacific (excluding China)
  EU = 'eu',  // Europe, Middle East, Africa
}

export interface TeslaRegionReply {
  response: {
    region: TeslaRegion
    fleet_api_base_url: string //eg 'https://fleet-api.prd.na.vn.cloud.tesla.com'
  }
}

export enum ConnectionState {
  Offline = 'offline',      // offline or asleep (esp Model S/X)
  Online = 'online',
  Asleep = 'asleep',
  Shutdown = 'shutdown',    // not sure - poll will timeout
  Unknown = 'unknown',      // we added this value for when there is an API problem
}

export enum TeslaAccessType {
  Owner = 'OWNER',
  Driver = 'DRIVER',
}

// we created this enum
export enum TeslaApiTier {
  Discovery = 'discovery',
  Basic = 'basic',
  Pro = 'pro',
  // Fleet
}

export interface Vehicle {
  id: number
  vehicle_id: number
  vin: string
  display_name: string //blank string if vehicle name has not been set by owner
  option_codes: string
  color: null  //I've only ever seen null here
  access_type: TeslaAccessType | null
  tokens: string[]
  state: ConnectionState  //online, offline, asleep etc
  in_service: boolean  //vehicle is in service mode - no commands possible
  id_s: string  //string version of id
  calendar_enabled: boolean
  api_version: number
  backseat_token: null
  backseat_token_updated_at: null
  ble_autopair_enrolled: boolean
}

// charging_state state transitions:
//
// when Disconnected then connected with power offered:
//   Disconnected -> NoPower (why?) -> Starting -> Charging
// when Disconnected then connected with no power offered:
//   Disconnected -> TODO
// when connected and no power offered, then power offered:
//   NoPower -> Starting -> Charging
// when connected and no power offered and asleep, then power offered:
//   n/a (takes a while to wake up) -> Charging
// when Charging then stop charging command:
//   Charging -> Stopped
// when Stopped then start charging command:
//   Stopped -> Starting -> Charging
// when Charging then no power offered:
//   Charging -> Stopped  (why not NoPower!?)
// when Charging then charge limit reached
//   Charging -> Complete
// when Complete, then charge limit increased
//   Complete -> Starting -> Charging

export enum ChargingState {
  Disconnected = 'Disconnected', //unplugged
  Starting = 'Starting',
  Charging = "Charging",
  NoPower = "NoPower", //charger is not offering power
  Stopped = "Stopped", // Stopped by API command
                       // Stopped also occurs when charging then no power offered
  Complete = 'Complete',  //charge limit reached, not charging
  // more?
}

export enum ChargePortLatch {
  Engaged = 'Engaged', //plug is locked to vehicle, or there is no plug
  Disengaged = 'Disengaged', //plug is inserted but not locked
  Blocked = 'Blocked', //??? seen on Model S when unplugged
}

export enum ChargeCableType {
  IEC = 'IEC',
  Invalid = '<invalid>',
  // more?
}

export enum GuiDistanceUnits {
  KmHr = 'km/hr',
  MiHr = 'mi/hr'
}

export enum GuiRangeDisplay {
  Rated = 'Rated',
  Ideal = 'Ideal', //Model S/X only
}

export enum ScheduledChargingMode {
  Off = 'Off',
  StartAt = 'StartAt',
  DepartBy = 'DepartBy'
}

export interface ChargeState {
  // TODO some fields not here
  battery_level: number  //eg 22 (percent)
  battery_range: number //miles eg 46.86
  charge_amps: number //v24+, the value set by set_charging_amps
  charge_current_request: number //amps the vehicle wants?
  charge_current_request_max: number //max amps the vehicle can ask for (usually equals charger_pilot_current)
  charge_enable_request: boolean
  charge_energy_added: number //kWh eg 8.69
  charge_limit_soc: number //percent eg 70
  charge_limit_soc_max: number //100
  charge_limit_soc_min: number //50
  charge_limit_soc_std: number //90
  charge_miles_added_ideal: number //eg 39.5
  charge_miles_added_rated: number //eg 39.5
  charge_port_cold_weather_mode: boolean //eg false
  charge_port_door_open: boolean //eg false
  charge_port_latch: ChargePortLatch //eg "Engaged"
  charge_rate: number //amps going to battery?
  charger_pilot_current: number //amps offered by charger. usually 16 when unplugged, have seen 24
  charger_actual_current: number //amps being taken from charger
  charger_phases: number | null //number of phases being used (1 = 1 phase, 2,3 = 3 phase), null when unplugged
  charger_power: number //kW
  charger_voltage: number //Volts
  charging_state: ChargingState | null
  conn_charge_cable: ChargeCableType
  est_battery_range: number  //miles
  fast_charger_brand: string //"Tesla" or "<invalid>" (non-Tesla)
  fast_charger_present: boolean
  fast_charger_type: string //"Combo"
  ideal_battery_range: number  //miles
  managed_charging_active: boolean
  managed_charging_start_time: null | string //value?
  managed_charging_user_canceled: boolean
  max_range_charge_counter: number
  minutes_to_full_charge: number
  not_enough_power_to_heat: boolean

  // NOTE: these schedule fields no longer provide all info since the schedule
  // upgrades in 2024.26.x
  // https://github.com/jbanyer/garnet/issues/1548
  off_peak_charging_enabled: boolean,
  off_peak_charging_times: string //'all_week' | ?
  off_peak_hours_end_time: number //minutes past midnight eg 360
  scheduled_charging_mode: ScheduledChargingMode
  scheduled_charging_pending: boolean
  scheduled_charging_start_time: number | null //unix time when scheduled charging will next start
  scheduled_charging_start_time_app: number //start-at time, minutes past midnight eg 360 = 06:00
  scheduled_charging_start_time_minutes?: number //time of next scheduled charge, minutes past midnight
  scheduled_departure_time: number //unix time, next depart by time (tomorrow)
  scheduled_departure_time_minutes: number //depart by time, minutes past midnight

  time_to_full_charge: number
  timestamp: number  //eg 1604977209418,
  trip_charging: boolean
  usable_battery_level: number
  user_charge_enable_request: null
}

export enum ShiftState {
  P = 'P',
  D = 'D',
  R = 'R',
  N = 'N',
}

export interface DriveState {
  // GPS location fields below are only supplied if we request them as of 2023.38+
  // see https://github.com/jbanyer/garnet/issues/1064
  gps_as_of?: number //eg 1543187664,
  heading?: number
  latitude?: number // 33.111111,
  longitude?: number // -88.111111,
  native_latitude?: number // 33.111111,
  native_location_supported?: number // 1,
  native_longitude?: number // -88.111111
  native_type?: string //"wgs"
  power: number,
  shift_state: ShiftState | null, //goes null on Model 3 after exiting vehicle?
  speed: number | null,
  timmestamp: number
}

export interface GuiSettings {
  "gui_24_hour_time": boolean //false,
  "gui_charge_rate_units": 'km/hr' | 'mi/hr' | 'kW' //reflects km/pct for battery level
  "gui_distance_units": GuiDistanceUnits
  "gui_range_display": GuiRangeDisplay
  "gui_temperature_units": "C" | "F"
  "gui_tirepressure_units": "Psi" //what else?
  "show_range_units": boolean //false - not sure what this does. Does NOT control km/pct for battery level
  "timestamp": number //1635212442718
}

// this example for a Model 3 SR+
export interface VehicleConfig {
  "can_accept_navigation_requests": boolean //true,
  "can_actuate_trunks": boolean //true,
  "car_special_type": string //"base",
  "car_type": string //"model3",
  "charge_port_type": string //"CCS",
  "default_charge_to_max": boolean //false,
  "driver_assist": string //"TeslaAP3",
  "ece_restrictions": boolean //false,
  "efficiency_package": string //"Default",
  "eu_vehicle": boolean //true,
  "exterior_color": string //"PearlWhite",
  "exterior_trim": string //"Chrome",
  "has_air_suspension": boolean //false,
  "has_ludicrous_mode": boolean //false,
  "interior_trim_type": string //"Black",
  "key_version": number //2,
  "motorized_charge_port": boolean //true,
  "performance_package": string //"BasePlus",
  "plg": boolean //false,
  "rear_drive_unit": string //"PM216MOSFET",
  "rear_seat_heaters": number //0,
  "rear_seat_type": number //0,
  "rhd": boolean //true,
  "roof_color": string //"RoofColorGlass",
  "seat_type": null,
  "spoiler_type": string //"None",
  "sun_roof_installed": null,
  "third_row_seats": string //"None",
  "timestamp": number //1635212442718,
  "trim_badging": string //"50",
  "use_range_badging": boolean //true,
  "wheel_type": string //"Pinwheel18"
}

export enum SoftwareUpdateStatus {
  None = '',
  Downloading = 'downloading',
  Installing = 'installing',
  Scheduled = 'scheduled'
}

export interface VehicleState {
  //....
  api_version: number //52
  car_version: string //"2023.2.10 4aa2cd7429ab"
  sentry_mode: boolean
  sentry_mode_available: boolean
  software_update: {
    download_perc: number //100,
    expected_duration_sec: number //1500,
    install_perc: number //10,
    scheduled_time_ms: number //1677242699164,
    status: SoftwareUpdateStatus
    version: string //'2023.2.12'
  }
  vehicle_name: string //'Model X', possibly set when vehicle has no name set
  //lot more...
}

// the data returned by the vehicle_data call
export interface AllVehicleState {
  id: number //3744408859774554,
  user_id: number //870762,
  vehicle_id: number //2252166956520795,
  vin: string // 'LRW3F7FSXNC718137',
  color: null,
  access_type: TeslaAccessType //'DRIVER',
  granular_access: { hide_private: boolean },
  tokens: string[] //[ '4c0ab494e974b4e0', '30723adb96cab7d0' ],
  state: ConnectionState // 'online',
  in_service: boolean //false, vehicle in service mode
  id_s: string //'3744408859774554',
  calendar_enabled: boolean //true,
  api_version: number //67,
  backseat_token: null,
  backseat_token_updated_at: null,
  ble_autopair_enrolled: boolean //false,
  charge_state: ChargeState
  //climate_state
  drive_state: DriveState
  gui_settings: GuiSettings
  vehicle_config: VehicleConfig
  vehicle_state: VehicleState
}

// subset of vehicle settings and properties that we are interested in,
// not including the charging-related ones in TeslaFields
export interface TeslaVehicleSettings {
  // ***** gui_settings *****
  "gui_charge_rate_units": 'km/hr' | 'mi/hr' | 'kW' //reflects km/pct for battery level
  "gui_distance_units": GuiDistanceUnits
  "gui_range_display": GuiRangeDisplay

  // ***** vehicle_config *****
  "car_type": string //"model3",

  // ***** vehicle state *****
  car_version: string //"2023.2.10 4aa2cd7429ab"
}

// subset of charging-related fields that we are interested in
export interface ChargeStateFields {
  // ***** charge_state *****
  battery_level: number  //eg 22 (percent) NOTE we set to usable_battery_level
  battery_range: number //miles eg 46.86 NOTE may be set to ideal_battery_range
  // charge_current_request: number
  // charge_current_request_max: number
  // charge_enable_request: boolean
  charge_amps: number //v24+, the value set by set_charging_amps
  charge_energy_added: number //kWh eg 8.69
  charge_limit_soc: number //percent eg 70
  // charge_limit_soc_max: number //100
  // charge_limit_soc_min: number //50
  // charge_limit_soc_std: number //90
  // charge_miles_added_ideal: number //eg 39.5
  // charge_miles_added_rated: number //eg 39.5
  // charge_port_cold_weather_mode: boolean //eg false
  // charge_port_door_open: boolean //eg false
  // charge_port_latch: ChargePortLatch //eg "Engaged"
  // charge_rate: number //amps going to battery?
  charger_pilot_current: number | null //amps offered by charger
  charger_actual_current: number | null //amps being taken from charger
  charger_phases: number | null //number of phases being used (1 = 1 phase, 2,3 = 3 phase), null when unplugged
  charger_power: number | null //kW (rounded to nearest kW)
  charger_voltage: number | null //Volts
  charging_state: ChargingState | null
  // conn_charge_cable: ChargeCableType
  // est_battery_range: number  //miles
  // fast_charger_brand: string //"Tesla" or "<invalid>" (non-Tesla)
  fast_charger_present: boolean | null
  // fast_charger_type: string //"Combo"
  // ideal_battery_range: number  //miles
  // managed_charging_active: boolean
  // managed_charging_start_time: null | string //value?
  // managed_charging_user_canceled: boolean
  // max_range_charge_counter: number
  minutes_to_full_charge: number
  // not_enough_power_to_heat: boolean

  // NOTE: these schedule fields no longer provide all info since the schedule
  // upgrades in 2024.26.x
  // https://github.com/jbanyer/garnet/issues/1548
  // below fields only set if they are active, to save space
  off_peak_charging_enabled?: boolean,
  off_peak_charging_times?: string //'all_week' | ?
  off_peak_hours_end_time?: number //minutes past midnight eg 360
  scheduled_charging_mode?: ScheduledChargingMode
  scheduled_charging_pending?: boolean
  //scheduled_charging_start_time?: number | null //unix time when scheduled charging will next start
  //scheduled_charging_start_time_app: number //start-at time, minutes past midnight eg 360 = 06:00
  scheduled_charging_start_time_minutes?: number //time of next scheduled charge, minutes past midnight
  //scheduled_departure_time: number //unix time, next depart by time (tomorrow)
  //scheduled_departure_time_minutes: number //depart by time, minutes past midnight

  // time_to_full_charge: number
  // timestamp: number  //eg 1604977209418,
  // trip_charging: boolean
  // usable_battery_level: number
  // user_charge_enable_request: null

  // these fields are derived from the api fields, and only set when necessary
  // the motivation is to save resources compared to always using the api fields
  chargePortUnlatched?: boolean //true when port is unlocked
}

export interface DriveStateFields {
  latitude?: number
  longitude?: number
  shift_state: ShiftState | null //null also means parked
}

export interface VehicleStateFields {
  sentryMode?: boolean //true if sentry mode enabled
  softwareUpdateStatus?: SoftwareUpdateStatus
}

export type TeslaFields = ChargeStateFields & DriveStateFields & VehicleStateFields

// the SSO tokens
// returned by https://auth.tesla.com/oauth2/v3/token
export interface TeslaSsoTokens {
  access_token: string
  refresh_token: string
  id_token: string
  expires_in: number //seconds, refers to the access_token and id_token
  token_type: string //"Bearer"
}

// the API tokens
// returned by https://owner-api.teslamotors.com/oauth/token
export interface TeslaApiTokens {
  access_token: string
  token_type: string //"bearer"
  expires_in: number //seconds
  refresh_token: string //not used
  created_at: number //seconds since epoch?
}

export enum CommandSigning {
  Required = 'required', //vehicle supports command signing
  Off = 'off', //vehicle does not support signed commands (pre-2021 Model S/X)
}

export interface VehicleProduct extends Vehicle {
  vehicle_config: VehicleConfig
  command_signing: CommandSigning
}

export enum ResourceType {
  Solar = 'solar', //solar-only, no powerwall
  Battery = 'battery', //Powerwall
}

export interface EnergySiteProduct {
  energy_site_id: number //eg 155021541022,
  resource_type: ResourceType //solar or battery
  id: string //eg 'STE20170815-99999' for Powerwall, uuid for solar only
  asset_site_id: string //uuid
  sync_grid_alert_enabled: boolean //false
  breaker_alert_enabled: boolean //false
}

export interface BatteryProduct extends EnergySiteProduct {
  site_name: string //eg"Jay's Powerwall",
  gateway_id: string //eg '9999999-01-F--T17G0004297',
  energy_left: number //eg 9030.578947368422,
  total_pack_energy: number //eg 13059,
  percentage_charged: number //eg 69.15214754091754,
  battery_type: string //eg 'ac_powerwall',
  backup_capable: boolean,
  battery_power: number //eg 700.0000000000001,
  components: {
    battery: boolean,
    battery_type: string //eg 'ac_powerwall',
    solar: boolean
    grid: boolean
    load_meter: boolean
    market_type: string //eg 'residential'
  }
}

// site with solar and gateway, no Powerwall
export interface SolarProduct extends EnergySiteProduct {
  solar_power: number //80 - what is this? kWp * 10?
  solar_type: string //'pv_panel'
  components: {
    battery: boolean //false
    solar: boolean //true
    solar_type: string //'pv_panel'
    grid: boolean //true
    load_meter: boolean //true
    market_type: string //'residential'
  }
}

export type ProductList = (BatteryProduct | SolarProduct | VehicleProduct)[]

// energy and power flows for an energy site.
// the same type is used for both solar-only and powerwall sites
export interface EnergySiteLiveStatus {
  // for all sites
  timestamp: string //eg '2021-10-18T12:33:04+11:00'
  solar_power: number //eg 12044.01025390625,

  // for sites with metering (I assume)
  load_power?: number //eg 4479.400146484375, consumption
  grid_status?: string //eg 'Active',
  grid_power?: number //eg -4334.610107421875, import is positive
  grid_services_active?: boolean,
  grid_services_power?: number //W eg 0, (vpp battery dispatch?)

  // sites with a battery
  energy_left?: number //Wh eg 12246.473684210525,
  total_pack_energy?: number //Wh eg 13077,
  percentage_charged?: number //eg 93.6489537677642,
  backup_capable?: boolean,
  battery_power?: number //W eg -3230, discharge is positive
  generator_power?: number //W? eg 0,
  island_status?: string //eg 'on_grid',
  storm_mode_active?: boolean,
}

// https://developer.tesla.com/docs/fleet-api#set_charge_limit
export interface SetChargeLimitRequest {
  percent: number //integer, eg 60 = 60%
}

// https://developer.tesla.com/docs/fleet-api#set_charging_amps
export interface SetChargingAmpsRequest {
  charging_amps: number //integer
}

export type TeslaCommandBody = SetChargeLimitRequest | SetChargingAmpsRequest

export enum TeslaCommandType {
  Start = 'Start',
  Stop = 'Stop',
  Wake = 'Wake',
  SetChargingAmps = 'SetChargingAmps',
  SetChargeLimit = 'SetChargeLimit',
}

// whether the vehicle is able to accept commands
export enum TeslaCommandReadiness {
  // values for the new fleet api
  Paired = 'paired',           // vehicle is accepting our signed commands
  NoKey = 'no-key',            // vehicle is rejecting our signed commands and requires pairing
  NoScope = 'no-scope',        // token is missing vehicle data or charging command scope
  Unsigned = 'unsigned',       // vehicle can not accept signed commands (pre-2021 Model S/X)
  // values for the old owners api
  OldApi = 'old-api',          // vehicle is accepting commands on the old api
  Deprecated = 'deprecated',   // vehicle will no longer accept old api commands
}

export interface TeslaCommandConfig {
  siteId: string
  id_s: string
  vin: string | null //only set for new api
  signed: boolean //should the command be signed? True for most vehicles, except old Model S/X
}

export interface TeslaCommandArgs {
  body?: TeslaCommandBody
  feature?: string //which Charge HQ feature caused the command?
}

// this reply is returned by Start, Stop, SetChargingAmps, SetChargeLimit
export interface TeslaCommandReply {
  // response is only set when there is no http error
  response: {
    result: boolean
    // I've only seen reason OR string set, not both
    reason?: string
    string?: string
  } | null
  // error fields are set when there is an http error
  error?: string
  error_description?: string
}

// the result of an attempt to send a tesla command
export interface TeslaCommandResult {
  http_code: number //0 if there was no http response
  reply: TeslaCommandReply
  // set when a 429 rate limit error is received, seconds until quota reset
  rateLimitReset?: number
}

// logged to MQTT
export interface TeslaApiCommand {
  site_id: string //uuid site.id
  fleet_api: boolean
  id_s: string
  command: TeslaCommandType
  value?: number //for commands with a numeric argument
  feature?: string
  send_tsms: number
  reply_ms: number //reply time in ms
  http_code: number //0 if there was no http response
  error?: string
  result?: boolean
  reason?: string
}
